import * as React from "react";
import { Link } from "react-router-dom";
import { m as motion, useReducedMotion } from "framer-motion";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, forgotPassword } from "@/lib/api";
import { TurnstileField } from "@/components/TurnstileField";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";

type ForgotState = {
  email: string;
  error: string;
  success: boolean;
  submitting: boolean;
  turnstileToken: string;
  turnstileResetKey: number;
};

type ForgotAction =
  | { type: "setEmail"; payload: string }
  | { type: "setError"; payload: string }
  | { type: "setSuccess"; payload: boolean }
  | { type: "setSubmitting"; payload: boolean }
  | { type: "setTurnstileToken"; payload: string }
  | { type: "resetTurnstile" };

function forgotReducer(state: ForgotState, action: ForgotAction): ForgotState {
  switch (action.type) {
    case "setEmail": return { ...state, email: action.payload };
    case "setError": return { ...state, error: action.payload };
    case "setSuccess": return { ...state, success: action.payload };
    case "setSubmitting": return { ...state, submitting: action.payload };
    case "setTurnstileToken": return { ...state, turnstileToken: action.payload };
    case "resetTurnstile": return { ...state, turnstileToken: "", turnstileResetKey: state.turnstileResetKey + 1 };
    default: return state;
  }
}

const initialForgot: ForgotState = {
  email: "",
  error: "",
  success: false,
  submitting: false,
  turnstileToken: "",
  turnstileResetKey: 0,
};

export function ForgotPasswordPage() {
  const prefersReducedMotion = useReducedMotion();
  const [state, dispatch] = React.useReducer(forgotReducer, initialForgot);
  const { email, error, success, submitting, turnstileToken, turnstileResetKey } = state;
  const fp = useDeviceFingerprint();

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    dispatch({ type: "setSubmitting", payload: true });
    dispatch({ type: "setError", payload: "" });
    dispatch({ type: "setSuccess", payload: false });
    try {
      await forgotPassword({ email, turnstile_token: turnstileToken, device_fingerprint: fp.fp || undefined, client_metadata: fp.meta });
      dispatch({ type: "setSuccess", payload: true });
      dispatch({ type: "setEmail", payload: "" });
      dispatch({ type: "setTurnstileToken", payload: "" });
    } catch (err) {
      dispatch({ type: "setError", payload: err instanceof ApiError ? err.message : "Request failed." });
      dispatch({ type: "resetTurnstile" });
    } finally {
      dispatch({ type: "setSubmitting", payload: false });
    }
  }

  return (
    <main className="grid min-h-screen place-items-center px-4 py-8">
      <motion.section
        className="w-full max-w-md"
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: prefersReducedMotion ? 0 : 0.25 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Reset Password</CardTitle>
            <CardDescription>
              Enter your email and we’ll send you a link to reset your password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="grid gap-4" onSubmit={handleSubmit}>
              {error && (
                <Alert className="border-destructive/20 bg-destructive/10 text-destructive">
                  <div className="mb-1 font-medium leading-none">Error</div>
                  <div className="text-sm">{error}</div>
                </Alert>
              )}
              {success && (
                <Alert className="border-success/20 bg-success/10 text-success">
                  <div className="mb-1 font-medium leading-none">Check your email</div>
                  <div className="text-sm">
                    If an account with that email exists, a reset link has been sent.
                  </div>
                </Alert>
              )}
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => dispatch({ type: "setEmail", payload: e.target.value })}
                  required
                />
              </div>
              <TurnstileField value={turnstileToken} onChange={(v) => dispatch({ type: "setTurnstileToken", payload: v })} resetKey={turnstileResetKey} />

              <Button type="submit" disabled={submitting || !turnstileToken}>
                {submitting ? "Sending…" : "Send Reset Link"}
              </Button>

              {/* Helpful guidance when an employee (or admin) hits the policy
                  restriction. The backend intentionally limits self-serve
                  password reset to subcontractors; employees use Entra. */}
              {error && /subcontractor/i.test(error) && (
                <div className="text-center text-sm">
                  <Button
                    type="button"
                    onClick={() => {
                      window.location.href = "/api/auth/entra/login";
                    }}
                  >
                    Use Synergy Fiber Sign In
                  </Button>
                  <p className="mt-2 text-muted-foreground">
                    Employee accounts sign in via the corporate domain on the login page. Only subcontractors may use email password reset.
                  </p>
                </div>
              )}
              <div className="text-center text-sm text-muted-foreground">
                <Link className="font-medium text-primary" to="/login">
                  Back to sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.section>
    </main>
  );
}
